import { GLSL_CONSTS, GLSL_HASH_NOISE, GLSL_JWST } from './common';

/**
 * Nebula / star pass (half-res RGBA8): 3-octave fbm nebula on the JWST ramp, hashed star field, and the
 * Droste log-polar infinite-zoom tunnel at the bindu. Rendered in css-pixel space (y down) so the main pass
 * can sample it with the raw screen UV.
 */
export const NEBULA_FRAG = /* glsl */ `#version 300 es
precision highp float;
in vec2 vUv;
out vec4 outColor;
uniform vec2 uCanvasCss;
uniform vec2 uCentre;
uniform float uR0;
uniform float uR;
uniform float uTime;
uniform float uDroste;
uniform float uContrast;
uniform float uTheme;
uniform vec3 uTint;
${GLSL_CONSTS}
${GLSL_HASH_NOISE}
${GLSL_JWST}
void main() {
  vec2 p = vUv * uCanvasCss;
  p.y = uCanvasCss.y - p.y;
  vec2 d = p - uCentre;
  float r = length(d);
  float a = atan(-d.y, d.x);
  float rn = r / max(uR, 1.0);

  // --- nebula body: two drifting fbm fields
  vec2 np = d / max(uR, 1.0) * 1.35;
  float n1 = fbm3(np + vec2(uTime * 0.011, -uTime * 0.007));
  float n2 = fbm3(np * 2.1 + vec2(5.2, 1.3) + vec2(-uTime * 0.006, uTime * 0.009));
  float dens = smoothstep(0.28, 0.9, n1 * 0.72 + n2 * 0.45);
  dens = pow(dens, 1.0 / max(uContrast, 0.1));
  // brightest in a halo around the mandala, fading toward the page corners, faint everywhere
  float radial = 0.2 + smoothstep(0.25, 1.05, rn) * (1.0 - smoothstep(1.35, 2.8, rn));
  vec3 col = jwst(dens * 0.78 + 0.04) * (0.3 + 0.7 * dens) * radial * 0.85;
  // colour breathing with the master centroid
  col *= mix(vec3(1.0), uTint * 1.25, 0.3);

  // --- stars: hashed grid, twinkling
  vec2 cell = floor(p / 6.0);
  vec2 cf = fract(p / 6.0) - 0.5;
  float h = hash21(cell);
  float h2 = hash21(cell + 13.7);
  float sr = 0.12 + 0.28 * h2;
  float star = step(0.955, h) * (1.0 - smoothstep(0.0, sr, length(cf - (vec2(h2, hash21(cell + 3.1)) - 0.5) * 0.5)));
  float twinkle = 0.55 + 0.45 * sin(uTime * (0.8 + h2 * 2.5) + h * 60.0);
  col += vec3(0.88, 0.9, 1.0) * star * twinkle * (0.5 + 0.5 * h2) * (0.35 + 0.65 * smoothstep(0.9, 1.4, rn));

  // --- Droste tunnel at the bindu: two log-polar layers cross-faded so the zoom period is invisible
  float tun = 1.0 - smoothstep(uR0 * 0.9, uR0 * 1.7, r);
  if (tun > 0.001) {
    float K = 3.2;
    float z = log(max(r, 0.25) / max(uR0, 1.0)) / log(K) + uTime * 0.16;
    float f0 = fract(z);
    float f1 = fract(z + 0.5);
    float w0 = 1.0 - abs(f0 - 0.5) * 2.0;
    vec2 dir = vec2(cos(a), sin(a));
    float l0 = fbm3(dir * pow(K, f0) * 2.4 + 3.1);
    float l1 = fbm3(dir * pow(K, f1) * 2.4 + 7.7);
    float tn = mix(l1, l0, w0);
    // spiral arms so the fall reads as rotation too
    float arms = 0.5 + 0.5 * sin(a * 3.0 + z * TAU * 0.5);
    vec3 tc = jwst(tn * 1.15 + arms * 0.18) * (0.55 + 0.45 * arms);
    float hole = smoothstep(0.0, uR0 * 0.55, r);
    tc = tc * hole * 1.1 + vec3(0.012, 0.008, 0.02);
    col = mix(col, tc, tun * uDroste);
  }

  // light theme: nebula desaturated to 20 %
  float lum = dot(col, vec3(0.299, 0.587, 0.114));
  col = mix(col, mix(vec3(lum), col, 0.2), uTheme);
  outColor = vec4(col, 1.0);
}
`;
